import { motion } from "framer-motion";
import { Building2, Activity } from "lucide-react";
import { SERVICES } from "./data";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-28 md:py-40">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">What we do</span>
            <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none text-foreground max-w-xl">
              A complete enrollment operation, run for you.
            </h2>
          </div>
          <p className="max-w-sm text-base text-muted-foreground leading-relaxed">
            Every service is delivered by specialists who live inside payer portals, turning administrative friction
            into confirmed effective dates.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                className={`${s.span} group relative border border-border bg-card p-8 md:p-10 flex flex-col justify-between min-h-[240px] transition-colors duration-300 hover:bg-secondary`}
                data-testid={`service-card-${i}`}
              >
                <Icon size={26} strokeWidth={1.4} className="text-primary" />
                <div className="mt-10">
                  <h3 className="font-serif text-2xl md:text-3xl text-foreground tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md">{s.desc}</p>
                </div>
                <span className="absolute top-8 right-8 font-mono text-xs text-muted-foreground/60">0{i + 1}</span>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5"
          data-testid="services-assurance"
        >
          <div className="border border-border bg-primary text-primary-foreground p-8 md:p-10">
            <Building2 size={26} strokeWidth={1.4} className="text-accent" />
            <h3 className="mt-8 font-serif text-2xl md:text-3xl tracking-tight">Handled entirely in house</h3>
            <p className="mt-3 text-sm text-primary-foreground/70 leading-relaxed max-w-md">
              Every service is performed by our own dedicated enrollment specialists, never outsourced or handed to a
              third party. Your provider data stays with one accountable team from start to finish.
            </p>
          </div>
          <div className="border border-border bg-card p-8 md:p-10">
            <Activity size={26} strokeWidth={1.4} className="text-primary" />
            <h3 className="mt-8 font-serif text-2xl md:text-3xl text-foreground tracking-tight">Transparent, live updates</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md">
              We share a live view with up-to-date statuses throughout the entire enrollment process, so you always
              know exactly where each application stands, no guessing, no waiting for a report.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
